import { useState } from "react";
import { ChevronDown, ChevronRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { FieldRenderer } from "./FieldRenderer";
import type { FieldGroupWithFields, FieldDefinition } from "@/types/fieldRegistry";

interface GroupedFieldFormProps {
  groupedFields: FieldGroupWithFields[];
  values: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
  disabled?: boolean;
  /** Filtrar campos por visibilidade: 'add_form' | 'detail' | 'all' */
  visibility?: 'add_form' | 'detail' | 'all';
  /** Se true, mostra apenas campos custom (não sistema) */
  customOnly?: boolean;
}

export function GroupedFieldForm({
  groupedFields,
  values,
  onChange,
  disabled,
  visibility = 'all',
  customOnly = false,
}: GroupedFieldFormProps) {
  const filteredGroups = groupedFields
    .map((gf) => ({
      ...gf,
      fields: filterFields(gf.fields, visibility, customOnly),
    }))
    .filter((gf) => gf.fields.length > 0);

  if (filteredGroups.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
        <p className="text-sm">Nenhum campo configurado</p>
        <p className="text-xs mt-1">Configure campos nas configurações da organização</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredGroups.map((gf) => (
        <FieldGroupSection
          key={gf.group.id}
          groupWithFields={gf}
          values={values}
          onChange={onChange}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

function filterFields(
  fields: FieldDefinition[],
  visibility: 'add_form' | 'detail' | 'all',
  customOnly: boolean
): FieldDefinition[] {
  let filtered = fields;

  if (customOnly) {
    filtered = filtered.filter((f) => !f.is_system);
  }

  switch (visibility) {
    case 'add_form':
      return filtered.filter((f) => f.is_visible_in_add_form);
    case 'detail':
      return filtered.filter((f) => f.is_visible_in_detail);
    default:
      return filtered;
  }
}

function FieldGroupSection({
  groupWithFields,
  values,
  onChange,
  disabled,
}: {
  groupWithFields: FieldGroupWithFields;
  values: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
  disabled?: boolean;
}) {
  const { group, fields } = groupWithFields;
  const [isOpen, setIsOpen] = useState(!group.is_default_collapsed);

  if (!group.is_collapsible) {
    return (
      <div className="space-y-3">
        <GroupHeader group={group} />
        <FieldList fields={fields} values={values} onChange={onChange} disabled={disabled} />
      </div>
    );
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between px-2 py-1.5 h-auto font-medium text-sm hover:bg-accent/50"
        >
          <div className="flex items-center gap-2">
            {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            <span>{group.name}</span>
            {group.is_system && <Lock className="h-3 w-3 text-muted-foreground" />}
          </div>
          <span className="text-xs text-muted-foreground">{fields.length} campos</span>
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pt-2 pl-6">
          <FieldList fields={fields} values={values} onChange={onChange} disabled={disabled} />
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

function GroupHeader({ group }: { group: FieldGroupWithFields['group'] }) {
  return (
    <div className="flex items-center gap-2 pb-1 border-b">
      <span className="font-medium text-sm">{group.name}</span>
      {group.is_system && <Lock className="h-3 w-3 text-muted-foreground" />}
    </div>
  );
}

function FieldList({
  fields,
  values,
  onChange,
  disabled,
}: {
  fields: FieldDefinition[];
  values: Record<string, any>;
  onChange: (fieldName: string, value: any) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-3">
      {fields.map((field) => {
        // Para campos sistema, o valor vem de values[system_column]
        // Para campos custom, vem de values.custom_data[field_name] ou values[field_name]
        const fieldValue = field.is_system && field.system_column
          ? values[field.system_column]
          : (values?.custom_data?.[field.field_name] ?? values?.[field.field_name]);

        return (
          <div key={field.id} className="space-y-1.5">
            <label className="text-sm font-medium leading-none">
              {field.field_label}
              {field.is_required && <span className="text-destructive ml-1">*</span>}
              {field.is_important && <span className="text-amber-500 ml-1">*</span>}
            </label>
            <FieldRenderer
              field={field}
              value={fieldValue ?? null}
              onChange={(value) => onChange(field.field_name, value)}
              disabled={disabled}
            />
          </div>
        );
      })}
    </div>
  );
}
